import { fetchEval } from './fetchEval'
import type { FactoryOrNull, ModuleLoaderFactory, ModuleFactory } from './types'
import { createPromise } from '../createPromise'

type ResolveDepsOptions = { url: string; moduleDependenciesIds: Array<string>; dependencies: Record<string, unknown> }
type ResolveDeps = (options: ResolveDepsOptions) => Array<unknown>

const isFactoryDefined = (factory: unknown): factory is ModuleFactory => typeof factory === 'function'

export const amdLoaderFactory: ModuleLoaderFactory = ({
	scriptsCache,
	fetch,
	defaultDependencies,
	withFetchRetry = true,
	globalThis = {},
}) => {
	const fetchPromisesCache: Record<string, Promise<Response>> = {}
	const resolveDeps: ResolveDeps = ({ url, moduleDependenciesIds, dependencies }) => {
		if (dependencies.globals) {
			return [dependencies.globals]
		}
		return moduleDependenciesIds.map((id: string) => {
			if (id === 'exports') {
				// Provide a fresh object for 'exports'
				return {}
			}
			if (!(id in dependencies)) {
				throw new Error(`Module "${url}" dependency "${id}" is missing from provided dependencies map`)
			}
			return dependencies[id]
		})
	}
	const createModuleInstance = (
		amdModuleFactory: FactoryOrNull,
		{ url, dependencies }: Omit<ResolveDepsOptions, 'moduleDependenciesIds'>
	) => {
		if (isFactoryDefined(amdModuleFactory)) {
			const deps = resolveDeps({
				url,
				dependencies,
				moduleDependenciesIds: amdModuleFactory.moduleDependenciesIds || [],
			})

			// If the first dependency is "exports", use the same object for both the factory and as the return value if needed
			if (amdModuleFactory.moduleDependenciesIds && amdModuleFactory.moduleDependenciesIds[0] === 'exports') {
				const exportsObj = deps[0] // This is the fresh object from resolveDeps
				const result = amdModuleFactory(exportsObj, ...deps.slice(1))
				return result === undefined ? exportsObj : result
			}

			return amdModuleFactory(...deps)
		}
		// Module bundle wasn't resolved to a factory, but to null
		return null
	}

	return {
		prefetchScript(url: string) {
			fetchPromisesCache[url] = fetchPromisesCache[url] || fetch(url)
		},
		loadModule: async (url, dependencies = {}, transform?: (code: string, url: string) => Promise<string>) => {
			const moduleDependencies = { ...defaultDependencies, ...dependencies }
			// When a module is requested during the fetch of the first,
			// we return a promise that would resolve to an instance for every caller
			const cached = scriptsCache[url]
			if (cached) {
				return createModuleInstance(await cached, { url, dependencies: moduleDependencies })
			}

			const { promise: currentLoadModulePromise, resolver } = createPromise<FactoryOrNull>()
			scriptsCache[url] = currentLoadModulePromise
			let originalDefineValue: any
			let moduleFactory: FactoryOrNull = null
			const defineAmdGlobals = () => {
				originalDefineValue = globalThis.define
				globalThis.define = (
					nameOrDependenciesIds: string | Array<string>,
					dependenciesIdsOrFactory: Array<string> | Function,
					factory: Function | undefined
				) => {
					const isNamedDefine = typeof nameOrDependenciesIds === 'string'
					// const moduleName = isNamedDefine ? args[0] : null
					const moduleDependenciesIds = ((isNamedDefine ? dependenciesIdsOrFactory : nameOrDependenciesIds) ||
						[]) as Array<string>
					const amdModuleFactory = (isNamedDefine ? factory : dependenciesIdsOrFactory) as ModuleFactory
					// save factory for caching
					moduleFactory = amdModuleFactory
					// save moduleDependenciesIds to use it when moduleFactory is cached.
					moduleFactory.moduleDependenciesIds = moduleDependenciesIds
				}
				globalThis.define.amd = true

				// When the platform runs inside isolated-renderer, the globalThis is not the same as self (since it doesn't run in a worker)
				// However, some app scripts still look for the define method specifically on 'self', so we need to create it there as well
				if (self && !self.define) {
					self.define = globalThis.define
				}
			}

			const cleanupAmdGlobals = () => {
				globalThis.define = originalDefineValue
			}

			const fetchWithCache = (fetchUrl: string) => fetchPromisesCache[fetchUrl] || fetch(fetchUrl)

			const fetchModule = () =>
				fetchEval(url, fetchWithCache, {
					beforeEval: defineAmdGlobals,
					afterEval: cleanupAmdGlobals,
					transform,
				})

			try {
				await fetchModule()
			} catch (e) {
				if (!withFetchRetry) {
					throw e
				}

				await fetchModule() // retry
			} finally {
				if (!moduleFactory) {
					// dont keep broken fetch / eval results in cache
					delete fetchPromisesCache[url]
					delete scriptsCache[url]
				}
				resolver(moduleFactory)
			}
			return createModuleInstance(await currentLoadModulePromise, { url, dependencies: moduleDependencies })
		},
	}
}
